/*
 * fn: filterTypeahead
 *
 * Filters grouped results of a typeahead component given a search term
 *
 */
import { type TypeaheadOptionGroupType, type TypeaheadOptionType } from '@/defs/input'
import { stripString } from '@/utils/commonUtil'

import { getHighlightNodes } from './getHighlightNodes'

type Props<T extends string = string> = {
  disableFiltering?: boolean
  minCharacters?: number
  optionGroups?: TypeaheadOptionGroupType<T>[]
  options?: TypeaheadOptionType<T>[]
  reset?: TypeaheadOptionType<T>
  search?: string
  value?: T
}

export const filterTypeahead = <T extends string = string>({
  disableFiltering,
  minCharacters = 0,
  optionGroups = [],
  options,
  reset,
  search,
  value,
}: Props<T>) => {
  const optionGroupsCombined = options ? [...optionGroups, { options }] : optionGroups
  const searchStripped = stripString(search)

  const baseGroups = optionGroupsCombined
    .map<TypeaheadOptionGroupType<T>>(({ Component, options }) => {
      if (!disableFiltering && searchStripped.length < minCharacters) return { options: [] }

      return {
        Component,
        options: options
          .filter(
            (option) =>
              disableFiltering ||
              [option.value, option.label].some((text) => stripString(text).includes(searchStripped)),
          )
          .map((option) => ({
            ...option,
            labelDisplay: getHighlightNodes(option.label || option.value, searchStripped),
          })),
      }
    })
    .filter((group) => (group.options.length || 0) > 0)

  const groups = reset && !!value ? [...baseGroups, { options: [reset] }] : baseGroups

  const resultsCount = groups.reduce((acc, group) => acc + (group.options.length || 0), 0)

  return {
    groups,
    resultsCount,
  }
}
